import axios from 'axios';
import { isLogEnabled } from '../../Utils/constants';
import { EVENTCODES, pushEventToParent } from '../../Utils/eventUtils';
import { captureException } from '../../Utils/sentryUtils';
import {
  getAltJourney,
  getRefreshToken,
  getSessionId,
  getTenant,
  getUserId,
  setAccessToken,
} from '../../Utils/storageUtils';
import { altJourneyType, TenantType } from '../../Utils/types';
import { showSessionTimeoutModal } from '../../Utils/utils';
import { AppBridge } from '../../App';

// Function to refresh the token (this should return a Promise with the new token)
const refreshAuthToken = async () => {
  try {
    const sessionId = getSessionId();
    const response = await pfmClientAxiosInstance.post(
      `${process.env.REACT_APP_PFM_CLIENT_BASEURL}/auth/refreshToken`,
      {
        refreshToken: getRefreshToken(),
        sessionId: sessionId,
        channelType:
          getTenant() === TenantType.YESBANK
            ? getAltJourney() === altJourneyType.YESONLINE
              ? 'ONLINE'
              : 'IRIS'
            : null,
      }
    );
    const { accessToken } = response.data;
    setAccessToken(accessToken);
    return accessToken;
  } catch (error: any) {
    captureException(error);
    console.error('Failed to refresh token:', error);
    throw error;
  }
};

function injectUserIdIntoUrl(url: string, userId: string): string {
  const pattern = /(\/(consent|dashboard))(\/[^/]+)/;
  return url.replace(pattern, `$1/${userId}$3`);
}

const pfmClientAxiosInstance = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
});

pfmClientAxiosInstance.interceptors.request.use(
  (config) => {
    if (
      config.url &&
      getTenant() === TenantType.YESBANK &&
      getUserId() &&
      !config.url.includes(`/${getUserId()}/`)
    ) {
      config.url = injectUserIdIntoUrl(config.url, getUserId()!);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

pfmClientAxiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (
      error.response &&
      error.response.status === 401 &&
      error.config.url.includes('/pfm-sdk') &&
      !error.config.url.includes('/auth/refreshToken')
    ) {
      try {
        const newAccessToken = await refreshAuthToken();
        error.config.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return pfmClientAxiosInstance(error.config);
      } catch (refreshError: any) {
        captureException(refreshError);
        if (isLogEnabled) {
          console.log('Refresh Token failed');
        }
        if (
          refreshError &&
          refreshError.response &&
          refreshError.response.data &&
          refreshError.response.data.errorCode &&
          refreshError.response.data.errorCode === 'HEARTBEAT_SESSION_EXPIRED'
        ) {
          pushEventToParent({ eventCode: EVENTCODES.SESSION_TIMEOUT_MDS });
        } else {
          pushEventToParent({ eventCode: 'SESSION_TIMEOUT' });
        }

        const { message } = await import('antd');
        message.error('Session Timeout');
        AppBridge.closeWebview();

        if (
          getTenant() === TenantType.YESBANK &&
          getAltJourney() === altJourneyType.YESONLINE
        ) {
          showSessionTimeoutModal();
        }
        // return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default pfmClientAxiosInstance;
